import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import "core-js/modules/es.array.map.js";
import { getIframeTargetOrigin } from '../utils/iframe';
import { generateInstanceId } from '../utils/id';
import { isWindowAvailable } from '../utils/window';
import { RequestIframeClientImpl } from '../impl/client';
import { RequestIframeServerImpl } from '../impl/server';
import { setRequestIframeLogLevel } from '../utils/logger';
import { Messages, ErrorCode, OriginConstant, LogLevel } from '../constants';
import { warnUnsafeTargetOriginForWindow } from '../utils/warnings';
import { ensureClientDebugInterceptors, loadDebugModule, wrapClientMethodsForDebug } from '../utils/debug-lazy';
import { applyStrictClientSecurityDefaults, applyStrictServerSecurityDefaults } from '../utils/strict-mode';

/**
 * Endpoint facade type (client + server).
 */

/**
 * Endpoint facade options (client + server).
 *
 * - For client-side sending: uses RequestIframeClientOptions fields (targetOrigin, headers, etc.)
 * - For server-side handling: uses RequestIframeServerOptions fields (middlewares, allowedOrigins, etc.)
 *
 * Note: overlapping fields (autoOpen/trace/allowedOrigins/validateOrigin/ackTimeout/autoAckMax*) apply to both sides.
 */

class RequestIframeEndpointApiFacade {
  constructor(params) {
    var _params$options;
    _defineProperty(this, "desiredOpen_", true);
    /** Client send APIs */
    _defineProperty(this, "send", (path, body, options) => this.getClient().send(path, body, options));
    _defineProperty(this, "sendFile", (path, content, options) => this.getClient().sendFile(path, content, options));
    _defineProperty(this, "sendStream", (path, stream, options) => this.getClient().sendStream(path, stream, options));
    _defineProperty(this, "isConnect", () => this.getClient().isConnect());
    _defineProperty(this, "getCookies", path => this.getClient().getCookies(path));
    _defineProperty(this, "getCookie", (name, path) => this.getClient().getCookie(name, path));
    _defineProperty(this, "setCookie", (name, value, options) => this.getClient().setCookie(name, value, options));
    _defineProperty(this, "removeCookie", (name, path) => this.getClient().removeCookie(name, path));
    _defineProperty(this, "clearCookies", () => this.getClient().clearCookies());
    /** Server routing/middleware APIs */
    _defineProperty(this, "use", (pathOrMiddleware, middleware) => this.getServer().use(pathOrMiddleware, middleware));
    _defineProperty(this, "on", (path, handler) => this.getServer().on(path, handler));
    _defineProperty(this, "off", path => this.getServer().off(path));
    _defineProperty(this, "map", handlers => this.getServer().map(handlers));
    this.targetWindow_ = params.targetWindow;
    this.targetOrigin_ = params.targetOrigin;
    this.options_ = params.options;
    this.endpointId_ = params.endpointId;
    this.desiredOpen_ = ((_params$options = params.options) === null || _params$options === void 0 ? void 0 : _params$options.autoOpen) !== false;
  }

  /**
   * Lazy create client.
   */
  getClient() {
    if (!this.client_) {
      var options = this.options_;
      var client = new RequestIframeClientImpl(this.targetWindow_, this.targetOrigin_, {
        secretKey: options === null || options === void 0 ? void 0 : options.secretKey,
        ackTimeout: options === null || options === void 0 ? void 0 : options.ackTimeout,
        timeout: options === null || options === void 0 ? void 0 : options.timeout,
        asyncTimeout: options === null || options === void 0 ? void 0 : options.asyncTimeout,
        returnData: options === null || options === void 0 ? void 0 : options.returnData,
        headers: options === null || options === void 0 ? void 0 : options.headers,
        allowedOrigins: options === null || options === void 0 ? void 0 : options.allowedOrigins,
        validateOrigin: options === null || options === void 0 ? void 0 : options.validateOrigin,
        autoOpen: false,
        autoAckMaxMetaLength: options === null || options === void 0 ? void 0 : options.autoAckMaxMetaLength,
        autoAckMaxIdLength: options === null || options === void 0 ? void 0 : options.autoAckMaxIdLength
      }, this.endpointId_);
      if (options !== null && options !== void 0 && options.trace) {
        var level = options.trace === true ? LogLevel.TRACE : options.trace;
        setRequestIframeLogLevel(level);
        if (level === LogLevel.TRACE || level === LogLevel.INFO) {
          wrapClientMethodsForDebug(client);
          void loadDebugModule().catch(() => {
            /** ignore */
          });
          void ensureClientDebugInterceptors(client);
        }
      }
      this.client_ = client;
    }
    if (this.desiredOpen_ && !this.client_.isOpen) {
      this.client_.open();
    }
    if (!this.desiredOpen_ && this.client_.isOpen) {
      this.client_.close();
    }
    return this.client_;
  }

  /**
   * Lazy create server.
   */
  getServer() {
    if (!this.server_) {
      var options = this.options_;
      var server = new RequestIframeServerImpl({
        secretKey: options === null || options === void 0 ? void 0 : options.secretKey,
        id: this.endpointId_,
        ackTimeout: options === null || options === void 0 ? void 0 : options.ackTimeout,
        autoOpen: false,
        allowedOrigins: options === null || options === void 0 ? void 0 : options.allowedOrigins,
        validateOrigin: options === null || options === void 0 ? void 0 : options.validateOrigin,
        maxConcurrentRequestsPerClient: options === null || options === void 0 ? void 0 : options.maxConcurrentRequestsPerClient,
        autoAckMaxMetaLength: options === null || options === void 0 ? void 0 : options.autoAckMaxMetaLength,
        autoAckMaxIdLength: options === null || options === void 0 ? void 0 : options.autoAckMaxIdLength
      });
      if (options !== null && options !== void 0 && options.trace) {
        var level = options.trace === true ? LogLevel.TRACE : options.trace;
        setRequestIframeLogLevel(level);
        if (level === LogLevel.TRACE || level === LogLevel.INFO) {
          void loadDebugModule().then(m => m.setupServerDebugListeners(server)).catch(() => {
            /** ignore */
          });
        }
      }
      this.server_ = server;
    }
    if (this.desiredOpen_ && !this.server_.isOpen) {
      this.server_.open();
    }
    if (!this.desiredOpen_ && this.server_.isOpen) {
      this.server_.close();
    }
    return this.server_;
  }

  /**
   * Optional accessors for debugging.
   * Note: accessing will trigger lazy creation.
   */
  get client() {
    return this.getClient();
  }
  get server() {
    return this.getServer();
  }

  /** Client fields */
  get targetWindow() {
    return this.targetWindow_;
  }
  get id() {
    return this.endpointId_;
  }
  get isOpen() {
    /**
     * Facade-level open state.
     * - When client/server is lazily created later, it will follow this state.
     */
    return this.desiredOpen_;
  }
  get interceptors() {
    return this.getClient().interceptors;
  }
  set interceptors(value) {
    this.getClient().interceptors = value;
  }
  isAvailable() {
    return isWindowAvailable(this.targetWindow_);
  }
  open() {
    var _this$client_, _this$server_;
    this.desiredOpen_ = true;
    (_this$client_ = this.client_) === null || _this$client_ === void 0 || _this$client_.open();
    (_this$server_ = this.server_) === null || _this$server_ === void 0 || _this$server_.open();
  }
  close() {
    var _this$client_2, _this$server_2;
    this.desiredOpen_ = false;
    (_this$client_2 = this.client_) === null || _this$client_2 === void 0 || _this$client_2.close();
    (_this$server_2 = this.server_) === null || _this$server_2 === void 0 || _this$server_2.close();
  }
  destroy() {
    var _this$client_3, _this$server_3;
    /**
     * Destroy created sides only.
     * They share the underlying MessageChannel via cache ref-counting, so partial creation is safe.
     */
    (_this$client_3 = this.client_) === null || _this$client_3 === void 0 || _this$client_3.destroy();
    (_this$server_3 = this.server_) === null || _this$server_3 === void 0 || _this$server_3.destroy();
    this.client_ = undefined;
    this.server_ = undefined;
  }
  /** Server fields */
  get secretKey() {
    var _this$options_;
    return (_this$options_ = this.options_) === null || _this$options_ === void 0 ? void 0 : _this$options_.secretKey;
  }
}

/**
 * Create an endpoint facade (client + server) for a peer window/iframe.
 *
 * It can:
 * - send requests to the peer (client)
 * - handle requests from the peer (server)
 */
export function requestIframeEndpoint(target, options) {
  var _applyStrictServerSec, _resolvedClient$optio, _resolvedClient$optio2, _resolvedOptions$id;
  var targetWindow = null;
  var defaultTargetOrigin = OriginConstant.ANY;
  if (target.tagName === 'IFRAME') {
    var iframe = target;
    targetWindow = iframe.contentWindow;
    defaultTargetOrigin = getIframeTargetOrigin(iframe);
    if (!targetWindow) {
      throw {
        message: Messages.IFRAME_NOT_READY,
        code: ErrorCode.IFRAME_NOT_READY
      };
    }
  } else {
    targetWindow = target;
    defaultTargetOrigin = OriginConstant.ANY;
  }
  var resolvedClient = applyStrictClientSecurityDefaults(defaultTargetOrigin, options);
  var targetOrigin = resolvedClient.targetOrigin;
  var resolvedOptions = (_applyStrictServerSec = applyStrictServerSecurityDefaults((_resolvedClient$optio = resolvedClient.options) !== null && _resolvedClient$optio !== void 0 ? _resolvedClient$optio : options)) !== null && _applyStrictServerSec !== void 0 ? _applyStrictServerSec : (_resolvedClient$optio2 = resolvedClient.options) !== null && _resolvedClient$optio2 !== void 0 ? _resolvedClient$optio2 : options;

  /**
   * P1: warn on unsafe default targetOrigin for Window targets.
   * Endpoint facade is lazy (client/server created later), so warn here.
   */
  warnUnsafeTargetOriginForWindow({
    isIframeTarget: target.tagName === 'IFRAME',
    targetOrigin,
    allowedOrigins: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.allowedOrigins,
    validateOrigin: resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.validateOrigin
  });

  /**
   * Endpoint uses ONE shared id by default, so it behaves like a single endpoint.
   * If options.id is provided, it becomes the shared id for both client+server.
   */
  var endpointId = (_resolvedOptions$id = resolvedOptions === null || resolvedOptions === void 0 ? void 0 : resolvedOptions.id) !== null && _resolvedOptions$id !== void 0 ? _resolvedOptions$id : generateInstanceId();
  return new RequestIframeEndpointApiFacade({
    targetWindow,
    targetOrigin,
    options: resolvedOptions,
    endpointId
  });
}